import { useCallback, useEffect, useMemo, useState } from "react";
import { Tooltip } from "antd";
import axios from "axios";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import AdminPagination from "@/components/admin/AdminPagination";
import AdminLayout from "@/components/layout/AdminLayout";
import AdminPageTitle from "@/components/admin/AdminPageTitle";
import { AdminSearchForm } from "@/components/admin/AdminTableToolbar";
import SourceMembersModal from "@/components/admin/SourceMembersModal";
import CreatableMultiSelect from "@/components/base/CreatableMultiSelect";
import ModalCloseButton from "@/components/base/ModalCloseButton";
import {
  MODAL_FOOTER_CANCEL_BUTTON_CLASS,
  MODAL_FOOTER_PRIMARY_BUTTON_CLASS,
  MODAL_FOOTER_PRIMARY_BUTTON_STYLE,
} from "@/components/base/modalFooterButtonStyles";
import SimpleMultiSelect from "@/components/base/SimpleMultiSelect";
import SimpleSelect from "@/components/base/SimpleSelect";
import NewStudyModal from "@/pages/admin/studies/components/NewStudyModal";
import StudySelect from "@/pages/admin/studies/components/StudySelect";
import { docCategoryApi, docTagApi, type DocTaxonomyItem } from "@/services/docTaxonomy";
import { createStudy, searchStudies, type Study, type StudySaveInput } from "@/services/studies";
import {
  createStudySource,
  deleteStudySource,
  getStudySourceGlobalExcludedKeywords,
  listStudySourceMembers,
  listStudySources,
  updateStudySource,
  updateStudySourceGlobalExcludedKeywords,
  type StudySourceConnectionType,
  type StudySourceItem,
  type StudySourceMember,
  type StudySourceSaveInput,
  type StudySourceTagBinding,
} from "@/services/studySource";
import ConfirmDialog from "../path-settings/ConfirmDialog";
import GlobalExcludedKeywords from "../path-settings/GlobalExcludedKeywords";
import SourceFormPageLayout, {
  SourceAuthorizationNotice,
  SourceFormField,
} from "../components/SourceFormPageLayout";
import { formatSharePointSitePreview } from "../path-settings/pathSettingsUtils";
import {
  getTeamsChannelName,
  getTeamsChannelUrlError,
  isTeamsChannelUrl,
} from "@/utils/teamsChannelUrl";

const CONNECTION_LABELS: Record<StudySourceConnectionType, string> = {
  0: "Teams Channel",
  1: "SharePoint Site",
};

const USER_SCOPE_LABELS: Record<0 | 1, string> = {
  0: "AZ Internal Only",
  1: "Internal & External",
};

const INLINE_ADD_BUTTON_CLASS =
  "inline-flex h-8 shrink-0 items-center gap-1 rounded-md border border-gray-200 px-2.5 text-[11px] font-medium text-gray-500 transition-colors hover:border-gray-300 hover:bg-gray-50 hover:text-gray-700";
const COMPACT_REMOVE_BUTTON_CLASS =
  "flex h-5 w-5 shrink-0 cursor-pointer items-center justify-center rounded text-gray-300 transition-colors hover:bg-red-50 hover:text-red-500";
const SOURCE_TAG_CLASS =
  "inline-flex max-w-full items-center rounded-md border border-gray-100 bg-gray-50/70 px-2.5 py-1.5 text-sm font-medium";
const SOURCE_LIST_PAGE_SIZE = 20;

function responseMessage(error: unknown, fallback: string) {
  if (axios.isAxiosError<{ message?: string }>(error)) {
    return error.response?.data?.message?.trim() || fallback;
  }
  return error instanceof Error && error.message ? error.message : fallback;
}

function createPathFields(paths: string[], fallback: string) {
  return paths.length > 0 ? [...paths] : [fallback];
}

function compactPathFields(paths: string[]) {
  return paths.map((path) => path.trim()).filter(Boolean);
}

function StudyPathInputList({
  label,
  values,
  placeholder,
  onChange,
}: {
  label: string;
  values: string[];
  placeholder: string;
  onChange: (values: string[]) => void;
}) {
  return (
    <div className="flex flex-col gap-2">
      {values.map((value, index) => {
        const isLast = index === values.length - 1;
        return (
          <div key={index} className="flex items-center gap-2">
            <input
              type="text"
              aria-label={`${label} ${index + 1}`}
              value={value}
              onChange={(event) =>
                onChange(
                  values.map((path, pathIndex) =>
                    pathIndex === index ? event.target.value : path,
                  ),
                )
              }
              placeholder={placeholder}
              className="min-w-0 flex-1 rounded-lg border border-gray-200 px-3.5 py-2.5 font-mono text-sm outline-none placeholder:font-sans focus:border-gray-400"
            />
            {isLast ? (
              <button
                type="button"
                aria-label={`Add ${label}`}
                onClick={() => onChange([...values, ""])}
                className={INLINE_ADD_BUTTON_CLASS}
              >
                <i className="ri-add-line text-sm leading-none" />
                <span>Add</span>
              </button>
            ) : (
              <button
                type="button"
                aria-label={`Remove ${label} ${index + 1}`}
                onClick={() => onChange(values.filter((_, pathIndex) => pathIndex !== index))}
                className={COMPACT_REMOVE_BUTTON_CLASS}
              >
                <i className="ri-delete-bin-line text-xs" />
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

async function loadAllStudies() {
  const firstPage = await searchStudies("", 1, 200);
  const studies = [...firstPage.data];
  const pageCount = Math.ceil(firstPage.count / firstPage.limit);
  for (let page = 2; page <= pageCount; page += 1) {
    const nextPage = await searchStudies("", page, 200);
    studies.push(...nextPage.data);
  }
  return studies;
}

function PathList({ paths, className }: { paths: string[]; className?: string }) {
  if (paths.length === 0) return <span className="text-xs text-gray-300">-</span>;
  return (
    <div className="flex flex-wrap items-center gap-1">
      {paths.map((path, index) => (
        <span
          key={`${path}-${index}`}
          className={`inline-flex items-center text-[10px] text-gray-500 font-mono rounded px-1.5 py-0.5 whitespace-nowrap ${className ?? ""}`}
        >
          {path}
        </span>
      ))}
    </div>
  );
}

export function StudySourceMembersModal({
  source,
  onClose,
}: {
  source: StudySourceItem;
  onClose: () => void;
}) {
  const [members, setMembers] = useState<StudySourceMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setLoadError("");
    void listStudySourceMembers(source.id)
      .then((result) => {
        if (active) setMembers(result.data);
      })
      .catch((error) => {
        if (active) setLoadError(responseMessage(error, "Failed to load Source members."));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [reloadKey, source.id]);

  return (
    <SourceMembersModal
      ariaLabel={`${source.studyCode} members`}
      contextLabel={`${source.studyCode} · ${CONNECTION_LABELS[source.connectionType]}`}
      members={members}
      loading={loading}
      loadError={loadError}
      onRetry={() => setReloadKey((current) => current + 1)}
      onClose={onClose}
    />
  );
}

interface StudySourceModalProps {
  presentation?: "modal" | "page";
  source: StudySourceItem | null;
  studies: Study[];
  categories: DocTaxonomyItem[];
  tags: DocTaxonomyItem[];
  submitting: boolean;
  serverError: string;
  onClearServerError?: () => void;
  onCancel: () => void;
  onSubmit: (input: StudySourceSaveInput) => Promise<void>;
}

export function StudySourceModal({
  presentation = "modal",
  source,
  studies,
  categories,
  tags,
  submitting,
  serverError,
  onClearServerError,
  onCancel,
  onSubmit,
}: StudySourceModalProps) {
  const isPage = presentation === "page";
  const [studyId, setStudyId] = useState(source?.studyId ?? studies[0]?.id ?? "");
  const [connectionType, setConnectionType] = useState<StudySourceConnectionType>(
    source?.connectionType ?? 0,
  );
  const [sourceUrl, setSourceUrl] = useState(source?.sourceUrl ?? "");
  const [sourceSite, setSourceSite] = useState(source?.sourceSite ?? "");
  const [sourceDrive, setSourceDrive] = useState(source?.sourceDrive ?? "");
  const [includedPaths, setIncludedPaths] = useState(() =>
    createPathFields(source?.includedPaths ?? [], "/"),
  );
  const [excludedPaths, setExcludedPaths] = useState(() =>
    createPathFields(source?.excludedPaths ?? [], ""),
  );
  const [remark, setRemark] = useState(source?.remark ?? "");
  const [agentTips, setAgentTips] = useState(source?.agentTips ?? "");
  const [userListSync, setUserListSync] = useState(source?.userListSync ?? true);
  const [userScope, setUserScope] = useState<0 | 1>(source?.userScope ?? 0);
  const [categoryIds, setCategoryIds] = useState<string[]>(source?.docCategoryIds ?? []);
  const [tagBindings, setTagBindings] = useState<StudySourceTagBinding[]>(
    source?.docTagBindings.length ? source.docTagBindings : [{ tagId: "", presetValues: [] }],
  );
  const [validationError, setValidationError] = useState("");
  const [createdStudies, setCreatedStudies] = useState<Study[]>([]);
  const [showNewStudy, setShowNewStudy] = useState(false);

  const availableStudies = useMemo(() => {
    const studiesById = new Map(studies.map((study) => [study.id, study]));
    createdStudies.forEach((study) => studiesById.set(study.id, study));
    return Array.from(studiesById.values());
  }, [createdStudies, studies]);
  const connectionOptions = Object.values(CONNECTION_LABELS);
  const categoryNameById = new Map(categories.map((category) => [category.id, category.name]));
  const categoryIdByName = new Map(categories.map((category) => [category.name, category.id]));
  const selectedCategoryNames = categoryIds
    .map((id) => categoryNameById.get(id))
    .filter((name): name is string => !!name);
  const tagNameById = new Map(tags.map((tag) => [tag.id, tag.name]));
  const tagIdByName = new Map(tags.map((tag) => [tag.name, tag.id]));
  const selectedTagBindings = tagBindings.filter((binding) => !!binding.tagId);
  const isSharePoint = connectionType === 1;
  const teamsChannelUrlInvalid =
    !isSharePoint && !!sourceUrl.trim() && !isTeamsChannelUrl(sourceUrl);
  const teamsChannelUrlError = teamsChannelUrlInvalid ? getTeamsChannelUrlError(sourceUrl) : "";
  const anchorFilled = !!sourceSite.trim() && (isSharePoint || isTeamsChannelUrl(sourceUrl));
  const canSubmit =
    !!studyId &&
    anchorFilled &&
    compactPathFields(includedPaths).length > 0 &&
    categoryIds.length > 0 &&
    selectedTagBindings.length > 0 &&
    !submitting;

  const handleSourceSiteChange = (value: string) => {
    setSourceSite(value);
    setValidationError("");
    onClearServerError?.();
  };

  const handleSubmit = async () => {
    setValidationError("");
    const normalizedIncludedPaths = compactPathFields(includedPaths);
    if (!studyId) return setValidationError("Study is required.");
    if (!isSharePoint && !sourceUrl.trim()) {
      return setValidationError("Teams Channel URL is required.");
    }
    if (!isSharePoint && !isTeamsChannelUrl(sourceUrl)) {
      return setValidationError(getTeamsChannelUrlError(sourceUrl));
    }
    if (!sourceSite.trim()) return setValidationError("Source Site is required.");
    if (normalizedIncludedPaths.length === 0) {
      return setValidationError("At least one Included Path is required.");
    }
    if (categoryIds.length === 0) return setValidationError("Doc Categories is required.");
    if (selectedTagBindings.length === 0) return setValidationError("Doc Tags is required.");

    await onSubmit({
      studyId,
      connectionType,
      ...(!isSharePoint ? { sourceUrl: sourceUrl.trim() } : {}),
      sourceSite: sourceSite.trim(),
      sourceDrive: sourceDrive.trim() || undefined,
      includedPaths: normalizedIncludedPaths,
      excludedPaths: compactPathFields(excludedPaths),
      remark: remark.trim() || undefined,
      agentTips: agentTips.trim() || undefined,
      userListSync,
      userScope,
      docCategoryIds: categoryIds,
      docTagBindings: selectedTagBindings.map((binding) => ({
        ...binding,
        presetValues: binding.presetValues
          .flatMap((value) => value.split(","))
          .map((value) => value.trim())
          .filter(Boolean),
      })),
    });
  };

  const handleCreateStudy = async (input: StudySaveInput) => {
    const study = await createStudy(input);
    setCreatedStudies((current) => [...current, study]);
    setStudyId(study.id);
    setShowNewStudy(false);
  };

  const newStudyModal = showNewStudy ? (
    <NewStudyModal onClose={() => setShowNewStudy(false)} onSave={handleCreateStudy} />
  ) : null;

  if (isPage) {
    return (
      <SourceFormPageLayout
        title={source ? "Edit Study Source" : "Add Study Source"}
        onBack={onCancel}
        notice={<SourceAuthorizationNotice />}
        primaryLabel={source ? "Save" : "Add"}
        primaryDisabled={!canSubmit}
        submitting={submitting}
        onPrimary={() => void handleSubmit()}
      >
        <SourceFormField label="Study" required>
          <StudySelect
            value={studyId}
            studies={availableStudies}
            onChange={setStudyId}
            onNewStudy={() => setShowNewStudy(true)}
          />
        </SourceFormField>

        <SourceFormField label="Connection" required>
          <div role="radiogroup" aria-label="Connection" className="flex flex-wrap gap-5 py-2">
            {(
              [
                { value: 0, label: CONNECTION_LABELS[0] },
                { value: 1, label: CONNECTION_LABELS[1] },
              ] as Array<{ value: StudySourceConnectionType; label: string }>
            ).map((option) => (
              <label
                key={option.value}
                className="flex cursor-pointer items-center gap-2 text-sm text-gray-700"
              >
                <input
                  type="radio"
                  name="study-source-connection-page"
                  value={option.value}
                  checked={connectionType === option.value}
                  onChange={() => setConnectionType(option.value)}
                  className="h-4 w-4 accent-[#830051]"
                />
                <span>{option.label}</span>
              </label>
            ))}
          </div>
        </SourceFormField>

        {!isSharePoint && (
          <SourceFormField label="Teams Channel URL" required>
            <input
              type="url"
              value={sourceUrl}
              onChange={(event) => setSourceUrl(event.target.value)}
              placeholder="https://teams.microsoft.com/l/channel/...?...groupId=..."
              aria-invalid={teamsChannelUrlInvalid}
              className={`w-full rounded-lg border px-3.5 py-2.5 font-mono text-sm text-gray-800 outline-none transition-colors placeholder:font-sans ${
                teamsChannelUrlInvalid
                  ? "border-red-300 focus:border-red-400"
                  : "border-gray-200 focus:border-gray-400"
              }`}
            />
            {teamsChannelUrlInvalid && (
              <p className="mt-1 text-xs text-red-600">{teamsChannelUrlError}</p>
            )}
          </SourceFormField>
        )}

        <SourceFormField label="Source Site" required>
          <input
            type="text"
            aria-label="Source Site"
            value={sourceSite}
            onChange={(event) => handleSourceSiteChange(event.target.value)}
            placeholder="e.g. HR-Library (segment after /sites/)"
            className="w-full rounded-lg border border-gray-200 px-3.5 py-2.5 font-mono text-sm text-gray-800 outline-none transition-colors placeholder:font-sans placeholder:text-gray-400 focus:border-gray-400"
          />
        </SourceFormField>

        <SourceFormField label="Source Drive">
          <input
            type="text"
            aria-label="Source Drive"
            value={sourceDrive}
            onChange={(event) => setSourceDrive(event.target.value)}
            placeholder="Document library name (leave blank for all libraries)"
            className="w-full rounded-lg border border-gray-200 px-3.5 py-2.5 font-mono text-sm text-gray-800 outline-none transition-colors placeholder:font-sans placeholder:text-gray-400 focus:border-gray-400"
          />
        </SourceFormField>

        <SourceFormField label="Description">
          <input
            type="text"
            maxLength={500}
            value={remark}
            onChange={(event) => setRemark(event.target.value)}
            placeholder="Optional description for users to understand this source"
            className="w-full rounded-lg border border-gray-200 bg-white px-3.5 py-2.5 text-sm outline-none transition-colors focus:border-gray-400"
          />
        </SourceFormField>

        <SourceFormField label="Doc Categories" required>
          <SimpleMultiSelect
            label=""
            values={selectedCategoryNames}
            options={categories.map((category) => category.name)}
            onChange={(names) =>
              setCategoryIds(
                names.map((name) => categoryIdByName.get(name)).filter((id): id is string => !!id),
              )
            }
            placeholder="Select categories from Admin Mgmt..."
            hideLabel
          />
        </SourceFormField>

        <SourceFormField label="Doc Tags" required>
          <div className="flex flex-col gap-3">
            {tagBindings.map((binding, index) => (
              <div key={`${binding.tagId || "new"}-${index}`} className="flex items-start gap-2">
                <div className="w-40 flex-shrink-0">
                  <SimpleSelect
                    label=""
                    value={tagNameById.get(binding.tagId) ?? ""}
                    options={tags
                      .filter(
                        (tag) =>
                          tag.id === binding.tagId ||
                          !selectedTagBindings.some((item) => item.tagId === tag.id),
                      )
                      .map((tag) => tag.name)}
                    onChange={(name) =>
                      setTagBindings((current) =>
                        current.map((item, bindingIndex) =>
                          bindingIndex === index
                            ? { ...item, tagId: tagIdByName.get(name) ?? "" }
                            : item,
                        ),
                      )
                    }
                    placeholder="Select tag..."
                    hideLabel
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <CreatableMultiSelect
                    label=""
                    values={binding.presetValues}
                    options={[]}
                    onChange={(presetValues) =>
                      setTagBindings((current) =>
                        current.map((item, bindingIndex) =>
                          bindingIndex === index ? { ...item, presetValues } : item,
                        ),
                      )
                    }
                    placeholder="Tag values..."
                    hideLabel
                    hideClearInDropdown
                  />
                </div>
                <button
                  type="button"
                  onClick={() =>
                    setTagBindings((current) =>
                      current.filter((_, bindingIndex) => bindingIndex !== index),
                    )
                  }
                  className={`${COMPACT_REMOVE_BUTTON_CLASS} mt-[9px]`}
                  aria-label={`Remove tag ${index + 1}`}
                >
                  <i className="ri-delete-bin-line text-xs" />
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() =>
                setTagBindings((current) => [...current, { tagId: "", presetValues: [] }])
              }
              className={`${INLINE_ADD_BUTTON_CLASS} w-fit`}
            >
              <i className="ri-add-line text-sm leading-none" />
              <span>Add tag</span>
            </button>
          </div>
        </SourceFormField>

        <SourceFormField
          label="Agent Tips"
          hint="These tips guide how the AI agent interprets and uses this source."
        >
          <textarea
            rows={4}
            maxLength={500}
            value={agentTips}
            onChange={(event) => setAgentTips(event.target.value)}
            placeholder="e.g. Prioritize current documents over archived ones."
            className="w-full resize-none rounded-lg border border-gray-200 bg-white px-3.5 py-2.5 text-sm outline-none transition-colors focus:border-gray-400"
          />
        </SourceFormField>

        <div className="border-t border-gray-100" />

        <SourceFormField label="Included Paths" required>
          <StudyPathInputList
            label="Included Path"
            values={includedPaths}
            placeholder="/Archive/HR-Library"
            onChange={setIncludedPaths}
          />
        </SourceFormField>

        <SourceFormField label="Excluded Paths">
          <StudyPathInputList
            label="Excluded Path"
            values={excludedPaths}
            placeholder="/Archive/HR-Library/legacy"
            onChange={setExcludedPaths}
          />
        </SourceFormField>

        <div className="border-t border-gray-100" />

        <SourceFormField label="User List Sync">
          <label
            className={`flex items-start gap-2.5 py-2 text-sm transition-colors ${
              userListSync ? "text-gray-800" : "text-gray-600"
            } cursor-pointer`}
          >
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 accent-[#830051]"
              checked={userListSync}
              onChange={(event) => setUserListSync(event.target.checked)}
            />
            <span>
              <span className="font-medium">Synchronize member list</span>
              <span className="mt-0.5 block text-xs text-gray-400">
                Synchronize the member list from this connection.
              </span>
            </span>
          </label>
        </SourceFormField>

        <SourceFormField label="User Scope">
          <div role="radiogroup" aria-label="User Scope" className="flex flex-wrap gap-5 py-2">
            {(
              [
                { value: 1, label: "Internal & External" },
                { value: 0, label: "AZ Internal Only" },
              ] as Array<{ value: 0 | 1; label: string }>
            ).map((option) => (
              <label
                key={option.value}
                className="flex cursor-pointer items-center gap-2 text-sm text-gray-700"
              >
                <input
                  type="radio"
                  name="study-source-user-scope-page"
                  value={option.value}
                  checked={userScope === option.value}
                  onChange={() => setUserScope(option.value)}
                  className="h-4 w-4 accent-[#830051]"
                />
                <span>{option.label}</span>
              </label>
            ))}
          </div>
        </SourceFormField>

        {(validationError || serverError) && (
          <div className="rounded-lg border border-red-100 bg-red-50 px-3 py-2 text-xs text-red-600 lg:ml-[184px]">
            {validationError || serverError}
          </div>
        )}

        {newStudyModal}
      </SourceFormPageLayout>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="study-source-modal-title"
        className="mx-4 flex max-h-[calc(100vh-2rem)] w-full max-w-3xl flex-col overflow-hidden rounded-xl bg-white shadow-sm"
      >
        <div className="flex flex-shrink-0 items-center gap-3 border-b border-gray-100 px-6 py-4">
          <div
            className="flex h-8 w-8 items-center justify-center rounded-lg"
            style={{ backgroundColor: "rgba(131,0,81,0.08)" }}
          >
            <i className="ri-add-circle-line text-base" style={{ color: "#830051" }} />
          </div>
          <div>
            <h3 id="study-source-modal-title" className="text-sm font-semibold text-gray-900">
              {source ? "Edit Study Source" : "Add Study Source"}
            </h3>
            <p className="mt-0.5 text-xs text-gray-400">
              Connect one Teams Channel or SharePoint Site to a Study.
            </p>
          </div>
          <ModalCloseButton ariaLabel="Close" disabled={submitting} onClick={onCancel} />
        </div>

        <div className="flex flex-1 flex-col gap-4 overflow-y-auto px-6 py-5">
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium text-gray-700">
              Study<span className="ml-0.5 text-red-500">*</span>
            </label>
            <StudySelect
              value={studyId}
              studies={availableStudies}
              onChange={setStudyId}
              onNewStudy={() => setShowNewStudy(true)}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium text-gray-700">
              Connection<span className="ml-0.5 text-red-500">*</span>
            </label>
            <SimpleSelect
              label=""
              value={CONNECTION_LABELS[connectionType]}
              options={connectionOptions}
              onChange={(label) => {
                const nextType = Object.entries(CONNECTION_LABELS).find(
                  ([, connectionLabel]) => connectionLabel === label,
                )?.[0];
                setConnectionType(Number(nextType ?? 0) as StudySourceConnectionType);
              }}
              hideLabel
            />
          </div>

          {!isSharePoint && (
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-medium text-gray-700">
                Teams Channel URL<span className="ml-0.5 text-red-500">*</span>
              </label>
              <input
                type="url"
                value={sourceUrl}
                onChange={(event) => setSourceUrl(event.target.value)}
                placeholder="https://teams.microsoft.com/l/channel/...?...groupId=..."
                aria-invalid={teamsChannelUrlInvalid}
                className={`w-full rounded-lg border px-3 py-2 font-mono text-sm text-gray-800 outline-none transition-colors placeholder:text-gray-300 ${
                  teamsChannelUrlInvalid
                    ? "border-red-300 focus:border-red-400"
                    : "border-gray-200 focus:border-gray-400"
                }`}
              />
              {teamsChannelUrlInvalid && (
                <p className="text-xs text-red-600">{teamsChannelUrlError}</p>
              )}
            </div>
          )}

          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium text-gray-700">
              Source Site<span className="ml-0.5 text-red-500">*</span>
            </label>
            <input
              type="text"
              aria-label="Source Site"
              value={sourceSite}
              onChange={(event) => handleSourceSiteChange(event.target.value)}
              placeholder="e.g. HR-Library (segment after /sites/)"
              className="w-full rounded-lg border border-gray-200 px-3 py-2 font-mono text-sm text-gray-800 outline-none transition-colors placeholder:text-gray-300 focus:border-gray-400"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium text-gray-700">Source Drive</label>
            <input
              type="text"
              aria-label="Source Drive"
              value={sourceDrive}
              onChange={(event) => setSourceDrive(event.target.value)}
              placeholder="Document library name (leave blank for all libraries)"
              className="w-full rounded-lg border border-gray-200 px-3 py-2 font-mono text-sm text-gray-800 outline-none transition-colors placeholder:text-gray-300 focus:border-gray-400"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium text-gray-700">Description</label>
            <input
              type="text"
              maxLength={500}
              value={remark}
              onChange={(event) => setRemark(event.target.value)}
              placeholder="Describe this source for users..."
              className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm outline-none transition-colors focus:border-gray-400"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium text-gray-700">
              Doc Categories<span className="ml-0.5 text-red-500">*</span>
            </label>
            <SimpleMultiSelect
              label=""
              values={selectedCategoryNames}
              options={categories.map((category) => category.name)}
              onChange={(names) =>
                setCategoryIds(
                  names
                    .map((name) => categoryIdByName.get(name))
                    .filter((id): id is string => !!id),
                )
              }
              placeholder="Select categories from Admin Mgmt..."
              hideLabel
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium text-gray-700">
              Doc Tags<span className="ml-0.5 text-red-500">*</span>
            </label>
            <div className="flex flex-col gap-3">
              {tagBindings.map((binding, index) => (
                <div key={`${binding.tagId || "new"}-${index}`} className="flex items-start gap-2">
                  <div className="w-40 flex-shrink-0">
                    <SimpleSelect
                      label=""
                      value={tagNameById.get(binding.tagId) ?? ""}
                      options={tags
                        .filter(
                          (tag) =>
                            tag.id === binding.tagId ||
                            !selectedTagBindings.some((item) => item.tagId === tag.id),
                        )
                        .map((tag) => tag.name)}
                      onChange={(name) =>
                        setTagBindings((current) =>
                          current.map((item, bindingIndex) =>
                            bindingIndex === index
                              ? { ...item, tagId: tagIdByName.get(name) ?? "" }
                              : item,
                          ),
                        )
                      }
                      placeholder="Select tag..."
                      hideLabel
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <CreatableMultiSelect
                      label=""
                      values={binding.presetValues}
                      options={[]}
                      onChange={(presetValues) =>
                        setTagBindings((current) =>
                          current.map((item, bindingIndex) =>
                            bindingIndex === index ? { ...item, presetValues } : item,
                          ),
                        )
                      }
                      placeholder="Tag values..."
                      hideLabel
                      hideClearInDropdown
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      setTagBindings((current) =>
                        current.filter((_, bindingIndex) => bindingIndex !== index),
                      )
                    }
                    className={`${COMPACT_REMOVE_BUTTON_CLASS} mt-[9px]`}
                    aria-label={`Remove tag ${index + 1}`}
                  >
                    <i className="ri-delete-bin-line text-xs" />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() =>
                  setTagBindings((current) => [...current, { tagId: "", presetValues: [] }])
                }
                className={`${INLINE_ADD_BUTTON_CLASS} w-fit`}
              >
                <i className="ri-add-line text-sm leading-none" />
                <span>Add tag</span>
              </button>
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium text-gray-700">Agent Tips</label>
            <textarea
              rows={4}
              maxLength={500}
              value={agentTips}
              onChange={(event) => setAgentTips(event.target.value)}
              placeholder="Instructions for the AI agent to better understand this source."
              className="w-full resize-none rounded-lg border border-gray-200 bg-white px-3.5 py-2.5 text-sm outline-none transition-colors focus:border-gray-400"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium text-gray-700">
              Included Paths<span className="ml-0.5 text-red-500">*</span>
            </label>
            <StudyPathInputList
              label="Included Path"
              values={includedPaths}
              placeholder="/Archive/HR-Library"
              onChange={setIncludedPaths}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium text-gray-700">Excluded Paths</label>
            <StudyPathInputList
              label="Excluded Path"
              values={excludedPaths}
              placeholder="/Archive/HR-Library/legacy"
              onChange={setExcludedPaths}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium text-gray-700">User List Sync</label>
            <label
              className={`flex items-start gap-2.5 rounded-lg border px-3.5 py-2.5 text-sm transition-colors ${
                userListSync
                  ? "border-[#830051]/40 bg-[#830051]/5 text-gray-800"
                  : "border-gray-200 bg-white text-gray-600"
              } cursor-pointer`}
            >
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 accent-[#830051]"
                checked={userListSync}
                onChange={(event) => setUserListSync(event.target.checked)}
              />
              <span>
                <span className="font-medium">Synchronize member list</span>
                <span className="mt-0.5 block text-xs text-gray-400">
                  Synchronize the member list from this connection.
                </span>
              </span>
            </label>
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium text-gray-700">User Scope</label>
            <div role="radiogroup" aria-label="User Scope" className="flex flex-wrap gap-3">
              {(
                [
                  { value: 1, label: "Internal & External" },
                  { value: 0, label: "AZ Internal Only" },
                ] as Array<{ value: 0 | 1; label: string }>
              ).map((option) => {
                const selected = userScope === option.value;
                return (
                  <label
                    key={option.value}
                    className={`flex min-w-52 flex-1 cursor-pointer items-center gap-2.5 rounded-lg border px-3.5 py-2.5 text-sm transition-colors ${
                      selected
                        ? "border-[#830051]/40 bg-[#830051]/5 text-gray-800"
                        : "border-gray-200 bg-white text-gray-600 hover:border-gray-300"
                    }`}
                  >
                    <input
                      type="radio"
                      name="study-source-user-scope"
                      value={option.value}
                      checked={selected}
                      onChange={() => setUserScope(option.value)}
                      className="h-4 w-4 accent-[#830051]"
                    />
                    <span>{option.label}</span>
                  </label>
                );
              })}
            </div>
          </div>

          {(validationError || serverError) && (
            <div className="rounded-lg border border-red-100 bg-red-50 px-3 py-2 text-xs text-red-600">
              {validationError || serverError}
            </div>
          )}
        </div>

        <div className="flex flex-shrink-0 items-center justify-end gap-2 border-t border-gray-100 px-6 py-4">
          <button
            type="button"
            disabled={submitting}
            onClick={onCancel}
            className={`${MODAL_FOOTER_CANCEL_BUTTON_CLASS} disabled:cursor-not-allowed disabled:opacity-50`}
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!canSubmit}
            onClick={() => void handleSubmit()}
            className={`${MODAL_FOOTER_PRIMARY_BUTTON_CLASS} inline-flex items-center gap-1.5 disabled:cursor-not-allowed disabled:opacity-40`}
            style={MODAL_FOOTER_PRIMARY_BUTTON_STYLE}
          >
            {submitting && <i className="ri-loader-4-line animate-spin" />}
            Save
          </button>
        </div>

        {newStudyModal}
      </div>
    </div>
  );
}

export default function StudySourceSettingsPage({
  pageMode = "list",
}: {
  pageMode?: "list" | "add" | "edit";
}) {
  const navigate = useNavigate();
  const { sourceId } = useParams<{ sourceId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedSourceId = searchParams.get("sourceId")?.trim() ?? "";
  const [sources, setSources] = useState<StudySourceItem[]>([]);
  const [studies, setStudies] = useState<Study[]>([]);
  const [categories, setCategories] = useState<DocTaxonomyItem[]>([]);
  const [tags, setTags] = useState<DocTaxonomyItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [docCategoryFilter, setDocCategoryFilter] = useState("all");
  const [docTagFilter, setDocTagFilter] = useState("all");
  const [studyFilter, setStudyFilter] = useState("all");
  const [connectionFilter, setConnectionFilter] = useState("all");
  const [userScopeFilter, setUserScopeFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [modalError, setModalError] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<StudySourceItem | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [actionError, setActionError] = useState("");
  const [globalKeywords, setGlobalKeywords] = useState<string[]>([]);
  const [globalKeywordsLoading, setGlobalKeywordsLoading] = useState(true);
  const [globalKeywordsError, setGlobalKeywordsError] = useState("");
  const [copiedSourceId, setCopiedSourceId] = useState<string | null>(null);
  const [membersTarget, setMembersTarget] = useState<StudySourceItem | null>(null);

  const clearRequestedSource = useCallback(() => {
    if (!requestedSourceId) return;
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.delete("sourceId");
      return next;
    });
  }, [requestedSourceId, setSearchParams]);

  const loadPage = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const [sourceResult, studyResult, categoryResult, tagResult] = await Promise.all([
        listStudySources(),
        loadAllStudies(),
        docCategoryApi.list(),
        docTagApi.list(),
      ]);
      setSources(sourceResult.data);
      setStudies(studyResult);
      setCategories(categoryResult);
      setTags(tagResult);
    } catch (error) {
      setLoadError(responseMessage(error, "Failed to load Study Sources."));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadGlobalKeywords = useCallback(async () => {
    setGlobalKeywordsLoading(true);
    setGlobalKeywordsError("");
    try {
      setGlobalKeywords(await getStudySourceGlobalExcludedKeywords());
    } catch (error) {
      setGlobalKeywordsError(responseMessage(error, "Failed to load global excluded keywords."));
    } finally {
      setGlobalKeywordsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadPage();
    if (pageMode === "list") void loadGlobalKeywords();
  }, [loadGlobalKeywords, loadPage, pageMode]);

  const categoryNameById = useMemo(
    () => Object.fromEntries(categories.map((category) => [category.id, category.name])),
    [categories],
  );
  const tagNameById = useMemo(
    () => Object.fromEntries(tags.map((tag) => [tag.id, tag.name])),
    [tags],
  );
  const availableCategoryFilters = useMemo(
    () =>
      categories.filter((category) =>
        sources.some((source) => source.docCategoryIds.includes(category.id)),
      ),
    [categories, sources],
  );
  const availableTagFilters = useMemo(
    () =>
      tags.filter((tag) =>
        sources.some((source) => source.docTagBindings.some((binding) => binding.tagId === tag.id)),
      ),
    [sources, tags],
  );
  const availableStudyFilters = useMemo(
    () =>
      Array.from(
        new Map(
          sources.map((source) => [
            source.studyId,
            { id: source.studyId, name: source.studyName, code: source.studyCode },
          ]),
        ).values(),
      ).sort((left, right) => (left.code || left.name).localeCompare(right.code || right.name)),
    [sources],
  );

  const filteredSources = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    return sources.filter((source) => {
      const matchesRequestedSource = !requestedSourceId || source.id === requestedSourceId;
      const matchesDocCategory =
        docCategoryFilter === "all" || source.docCategoryIds.includes(docCategoryFilter);
      const matchesDocTag =
        docTagFilter === "all" ||
        source.docTagBindings.some((binding) => binding.tagId === docTagFilter);
      const matchesStudy = studyFilter === "all" || source.studyId === studyFilter;
      const matchesConnection =
        connectionFilter === "all" || String(source.connectionType) === connectionFilter;
      const matchesUserScope =
        userScopeFilter === "all" || String(source.userScope) === userScopeFilter;
      const matchesSearch =
        !keyword ||
        source.studyName.toLowerCase().includes(keyword) ||
        source.studyCode.toLowerCase().includes(keyword) ||
        source.sourceUrl.toLowerCase().includes(keyword) ||
        source.sourceSite.toLowerCase().includes(keyword) ||
        source.sourceDrive.toLowerCase().includes(keyword) ||
        source.includedPaths.some((path) => path.toLowerCase().includes(keyword)) ||
        source.excludedPaths.some((path) => path.toLowerCase().includes(keyword));
      return (
        matchesRequestedSource &&
        matchesDocCategory &&
        matchesDocTag &&
        matchesStudy &&
        matchesConnection &&
        matchesUserScope &&
        matchesSearch
      );
    });
  }, [
    connectionFilter,
    docCategoryFilter,
    docTagFilter,
    requestedSourceId,
    search,
    sources,
    studyFilter,
    userScopeFilter,
  ]);
  const totalPages = Math.max(1, Math.ceil(filteredSources.length / SOURCE_LIST_PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paginatedSources = filteredSources.slice(
    (currentPage - 1) * SOURCE_LIST_PAGE_SIZE,
    currentPage * SOURCE_LIST_PAGE_SIZE,
  );

  useEffect(() => {
    setPage(1);
  }, [
    connectionFilter,
    docCategoryFilter,
    docTagFilter,
    requestedSourceId,
    search,
    studyFilter,
    userScopeFilter,
  ]);

  useEffect(() => {
    setPage((current) => Math.min(current, totalPages));
  }, [totalPages]);

  const editSource =
    pageMode === "edit" && sourceId
      ? (sources.find((source) => source.id === sourceId) ?? null)
      : null;

  const saveSource = async (input: StudySourceSaveInput) => {
    setSubmitting(true);
    setModalError("");
    try {
      if (pageMode === "edit" && !editSource) return;
      const saved = editSource
        ? await updateStudySource(editSource.id, input)
        : await createStudySource(input);
      if (pageMode !== "list") {
        navigate("/admin/settings/study-source");
        return;
      }
      setSources((current) =>
        editSource
          ? current.map((source) => (source.id === saved.id ? saved : source))
          : [...current, saved],
      );
    } catch (error) {
      setModalError(responseMessage(error, "Failed to save Study Source."));
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setActionError("");
    try {
      await deleteStudySource(deleteTarget.id);
      setSources((current) => current.filter((source) => source.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (error) {
      setActionError(responseMessage(error, "Failed to delete Study Source."));
    } finally {
      setDeleting(false);
    }
  };

  if (pageMode !== "list") {
    return (
      <AdminLayout>
        {loading ? (
          <div className="px-6 py-20 text-center text-sm text-gray-400">
            <i className="ri-loader-4-line mb-2 inline-block animate-spin text-2xl" />
            <div>Loading source form...</div>
          </div>
        ) : loadError ? (
          <div className="rounded-xl border border-red-100 bg-red-50 px-4 py-4 text-sm text-red-600">
            <span>{loadError}</span>
            <button
              type="button"
              className="ml-3 font-medium underline"
              onClick={() => void loadPage()}
            >
              Retry
            </button>
          </div>
        ) : pageMode === "edit" && !editSource ? (
          <div className="px-6 py-20 text-center text-sm text-gray-500">
            <div>Study Source not found.</div>
            <button
              type="button"
              className="mt-3 font-medium text-[#830051] underline"
              onClick={() => navigate("/admin/settings/study-source")}
            >
              Back to Study Sources
            </button>
          </div>
        ) : (
          <StudySourceModal
            presentation="page"
            source={editSource}
            studies={studies}
            categories={categories}
            tags={tags}
            submitting={submitting}
            serverError={modalError}
            onClearServerError={() => setModalError("")}
            onCancel={() => {
              if (!submitting) navigate("/admin/settings/study-source");
            }}
            onSubmit={saveSource}
          />
        )}
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="mb-5 flex items-start justify-between gap-4">
        <div>
          <AdminPageTitle
            title="Study Source Settings"
            tooltip="Manage Teams Channel and SharePoint Site sources for each Study."
          />
        </div>
        <button
          type="button"
          onClick={() => navigate("/admin/settings/study-source/add")}
          className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg px-4 py-2 text-sm font-medium text-white transition-opacity hover:opacity-90"
          style={{ backgroundColor: "#830051" }}
        >
          <i className="ri-add-line" /> Add Study Source
        </button>
      </div>

      <GlobalExcludedKeywords
        keywords={globalKeywords}
        loading={globalKeywordsLoading}
        loadError={globalKeywordsError}
        onRetry={() => void loadGlobalKeywords()}
        onSave={async (keywords) => {
          setGlobalKeywords(await updateStudySourceGlobalExcludedKeywords(keywords));
        }}
      />

      {(loadError || actionError) && (
        <div className="mb-4 flex items-center justify-between gap-3 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-600">
          <span>{loadError || actionError}</span>
          {loadError && (
            <button type="button" className="font-medium underline" onClick={() => void loadPage()}>
              Retry
            </button>
          )}
        </div>
      )}

      {/* Search & Filter Bar — 与 General Source 保持一致 */}
      <div className="mb-4 flex flex-col items-start gap-3 sm:flex-row sm:items-center">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <select
              aria-label="Doc Categories"
              value={docCategoryFilter}
              onChange={(event) => {
                clearRequestedSource();
                setDocCategoryFilter(event.target.value);
              }}
              className="appearance-none text-xs border border-gray-200 rounded-lg pl-3 pr-8 py-1.5 outline-none focus:border-gray-400 bg-white cursor-pointer"
            >
              <option value="all">All Doc Categories</option>
              {availableCategoryFilters.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
            <i className="ri-arrow-down-s-line absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs pointer-events-none" />
          </div>
          <div className="relative">
            <select
              aria-label="Doc Tags"
              value={docTagFilter}
              onChange={(event) => {
                clearRequestedSource();
                setDocTagFilter(event.target.value);
              }}
              className="appearance-none text-xs border border-gray-200 rounded-lg pl-3 pr-8 py-1.5 outline-none focus:border-gray-400 bg-white cursor-pointer"
            >
              <option value="all">All Doc Tags</option>
              {availableTagFilters.map((tag) => (
                <option key={tag.id} value={tag.id}>
                  {tag.name}
                </option>
              ))}
            </select>
            <i className="ri-arrow-down-s-line absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs pointer-events-none" />
          </div>
          <div className="relative">
            <select
              aria-label="Study"
              value={studyFilter}
              onChange={(event) => {
                clearRequestedSource();
                setStudyFilter(event.target.value);
              }}
              className="appearance-none text-xs border border-gray-200 rounded-lg pl-3 pr-8 py-1.5 outline-none focus:border-gray-400 bg-white cursor-pointer"
            >
              <option value="all">All Studies</option>
              {availableStudyFilters.map((study) => (
                <option key={study.id} value={study.id}>
                  {study.code || study.name}
                </option>
              ))}
            </select>
            <i className="ri-arrow-down-s-line absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs pointer-events-none" />
          </div>
          <div className="relative">
            <select
              aria-label="Connection"
              value={connectionFilter}
              onChange={(event) => {
                clearRequestedSource();
                setConnectionFilter(event.target.value);
              }}
              className="cursor-pointer appearance-none rounded-lg border border-gray-200 bg-white py-1.5 pl-3 pr-8 text-xs outline-none focus:border-gray-400"
            >
              <option value="all">All Connections</option>
              <option value="0">Teams Channel</option>
              <option value="1">SharePoint Site</option>
            </select>
            <i className="ri-arrow-down-s-line pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-400" />
          </div>
          <div className="relative">
            <select
              aria-label="User Scope"
              value={userScopeFilter}
              onChange={(event) => {
                clearRequestedSource();
                setUserScopeFilter(event.target.value);
              }}
              className="appearance-none text-xs border border-gray-200 rounded-lg pl-3 pr-8 py-1.5 outline-none focus:border-gray-400 bg-white cursor-pointer"
            >
              <option value="all">All Scopes</option>
              <option value="1">Internal &amp; External</option>
              <option value="0">AZ Internal Only</option>
            </select>
            <i className="ri-arrow-down-s-line absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs pointer-events-none" />
          </div>
        </div>
        <AdminSearchForm
          value={searchInput}
          onChange={setSearchInput}
          onSearch={() => {
            clearRequestedSource();
            setSearch(searchInput);
          }}
          searchLabel="Search"
          placeholder="Search Study, code, Channel URL or path"
          showReset={
            requestedSourceId !== "" ||
            search !== "" ||
            searchInput !== "" ||
            docCategoryFilter !== "all" ||
            docTagFilter !== "all" ||
            studyFilter !== "all" ||
            connectionFilter !== "all" ||
            userScopeFilter !== "all"
          }
          onReset={() => {
            clearRequestedSource();
            setSearch("");
            setSearchInput("");
            setDocCategoryFilter("all");
            setDocTagFilter("all");
            setStudyFilter("all");
            setConnectionFilter("all");
            setUserScopeFilter("all");
          }}
          resetLabel="Reset"
        />
      </div>

      {loading ? (
        <div className="overflow-hidden rounded-xl border border-gray-100 bg-white">
          <div className="px-6 py-16 text-center text-sm text-gray-400">
            <i className="ri-loader-4-line mb-2 inline-block animate-spin text-2xl" />
            <div>Loading Study Sources...</div>
          </div>
        </div>
      ) : filteredSources.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-xl border border-gray-100 bg-white py-20 text-center">
          <i className="ri-microscope-line mb-3 text-3xl text-gray-200" />
          <p className="text-sm font-medium text-gray-400">No Study Sources found</p>
          <p className="mt-1 text-xs text-gray-300">Add a source or adjust the current filters.</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-gray-100 bg-white">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1620px] table-auto font-sans text-sm">
              <colgroup>
                <col style={{ width: 220 }} />
                <col style={{ width: 150 }} />
                <col style={{ width: 280 }} />
                <col />
                <col />
                <col style={{ width: 180 }} />
                <col style={{ width: 180 }} />
                <col style={{ width: 150 }} />
                <col style={{ width: 96 }} />
              </colgroup>
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50 text-xs font-semibold text-gray-500">
                  <th className="px-5 py-3 text-left" style={{ minWidth: 220 }}>
                    Study
                  </th>
                  <th className="px-5 py-3 text-left" style={{ minWidth: 150 }}>
                    Connection
                  </th>
                  <th className="px-5 py-3 text-left" style={{ minWidth: 280 }}>
                    Source
                  </th>
                  <th className="px-5 py-3 text-left" style={{ minWidth: 220 }}>
                    Included Paths
                  </th>
                  <th className="px-5 py-3 text-left" style={{ minWidth: 200 }}>
                    Excluded Paths
                  </th>
                  <th className="px-5 py-3 text-left" style={{ minWidth: 180 }}>
                    Doc Categories
                  </th>
                  <th className="px-5 py-3 text-left" style={{ minWidth: 180 }}>
                    Doc Tags
                  </th>
                  <th className="px-5 py-3 text-left" style={{ minWidth: 150 }}>
                    User Scope
                  </th>
                  <th
                    className="sticky right-0 z-20 border-l border-gray-100 bg-gray-50 px-5 py-3 text-right"
                    style={{ minWidth: 96 }}
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {paginatedSources.map((source) => (
                  <tr
                    key={source.id}
                    className="group align-top transition-colors hover:bg-gray-50"
                  >
                    <td className="px-5 py-3">
                      <div className="inline-flex max-w-full min-w-0 items-center gap-2">
                        <div
                          className="min-w-0 truncate text-sm font-medium text-gray-800"
                          title={source.studyName}
                        >
                          {source.studyName || "Unknown Study"}
                        </div>
                        <button
                          type="button"
                          title={copiedSourceId === source.id ? "Copied!" : "Copy SharePoint link"}
                          className="flex h-6 w-6 shrink-0 cursor-pointer items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                          onClick={(event) => {
                            event.stopPropagation();
                            const url = formatSharePointSitePreview(source.sourceSite);
                            void navigator.clipboard.writeText(url);
                            setCopiedSourceId(source.id);
                            setTimeout(
                              () =>
                                setCopiedSourceId((current) =>
                                  current === source.id ? null : current,
                                ),
                              1500,
                            );
                          }}
                        >
                          <i
                            className={`text-sm ${
                              copiedSourceId === source.id
                                ? "ri-check-line text-green-600"
                                : "ri-file-copy-line"
                            }`}
                          />
                        </button>
                      </div>
                      <div className="mt-1 text-[11px] text-gray-400">{source.studyCode}</div>
                      {source.remark && (
                        <div className="mt-1 line-clamp-2 text-[11px] leading-relaxed text-gray-400">
                          {source.remark}
                        </div>
                      )}
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex flex-nowrap items-center gap-1.5">
                        <span className="inline-flex whitespace-nowrap rounded-md bg-gray-100 px-2 py-1 text-[11px] text-gray-600">
                          {CONNECTION_LABELS[source.connectionType]}
                        </span>
                        {source.userListSync && (
                          <Tooltip
                            title="View synchronized members from this connection."
                            placement="top"
                            mouseEnterDelay={0}
                          >
                            <button
                              type="button"
                              aria-label={`View members for ${source.studyCode}`}
                              onClick={() => setMembersTarget(source)}
                              className="inline-flex h-6 w-6 shrink-0 cursor-pointer items-center justify-center rounded-md text-base leading-none text-emerald-600 transition-colors hover:bg-emerald-50 hover:text-emerald-700"
                            >
                              <span role="img" aria-label="User List Sync">
                                <i className="ri-group-line" aria-hidden="true" />
                              </span>
                            </button>
                          </Tooltip>
                        )}
                      </div>
                    </td>
                    <td className="px-5 py-3">
                      {source.connectionType === 1 ? (
                        <span
                          title={[source.sourceSite, source.sourceDrive]
                            .filter(Boolean)
                            .join(" / ")}
                          className={`${SOURCE_TAG_CLASS} text-gray-600`}
                        >
                          <span className="truncate">
                            {source.sourceSite || "-"}
                            {source.sourceDrive ? ` / ${source.sourceDrive}` : ""}
                          </span>
                        </span>
                      ) : (
                        <a
                          href={source.sourceUrl}
                          target="_blank"
                          rel="noreferrer"
                          title={source.sourceUrl}
                          className={`${SOURCE_TAG_CLASS} text-[#830051] transition-colors hover:border-gray-200 hover:bg-gray-100`}
                        >
                          <span className="truncate">
                            {source.channelNameFromUrl ||
                              getTeamsChannelName(source.sourceUrl) ||
                              source.sourceUrl}
                          </span>
                        </a>
                      )}
                    </td>
                    <td className="px-5 py-3">
                      <PathList paths={source.includedPaths} className="bg-gray-100" />
                    </td>
                    <td className="px-5 py-3">
                      <PathList paths={source.excludedPaths} className="bg-red-50" />
                    </td>
                    <td className="px-5 py-3">
                      {source.docCategoryIds.some((id) => categoryNameById[id]) ? (
                        <div className="flex flex-wrap items-center gap-1">
                          {source.docCategoryIds.map((id) =>
                            categoryNameById[id] ? (
                              <span
                                key={id}
                                className="inline-flex items-center rounded bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-600"
                              >
                                {categoryNameById[id]}
                              </span>
                            ) : null,
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-300">-</span>
                      )}
                    </td>
                    <td className="px-5 py-3">
                      {source.docTagBindings.some((binding) => tagNameById[binding.tagId]) ? (
                        <div className="flex flex-wrap items-center gap-1">
                          {source.docTagBindings.map((binding) =>
                            tagNameById[binding.tagId] ? (
                              <span
                                key={binding.tagId}
                                className="inline-flex items-center rounded bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-600"
                              >
                                {tagNameById[binding.tagId]}
                              </span>
                            ) : null,
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-300">-</span>
                      )}
                    </td>
                    <td className="px-5 py-3">
                      <span className="text-xs text-gray-600">
                        {USER_SCOPE_LABELS[source.userScope]}
                      </span>
                    </td>
                    <td className="sticky right-0 z-10 border-l border-gray-100 bg-white px-5 py-3 transition-colors group-hover:bg-gray-50">
                      <div className="flex items-center justify-end gap-1.5">
                        <button
                          type="button"
                          aria-label={`Edit ${source.studyCode}`}
                          onClick={() =>
                            navigate(
                              `/admin/settings/study-source/${encodeURIComponent(source.id)}/edit`,
                            )
                          }
                          className="flex h-7 w-7 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
                        >
                          <i className="ri-pencil-line text-sm" />
                        </button>
                        <button
                          type="button"
                          aria-label={`Delete ${source.studyCode}`}
                          onClick={() => setDeleteTarget(source)}
                          className="flex h-7 w-7 items-center justify-center rounded-md text-red-400 transition-colors hover:bg-red-50 hover:text-red-600"
                        >
                          <i className="ri-delete-bin-line text-sm" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      <AdminPagination
        total={filteredSources.length}
        page={currentPage}
        pageSize={SOURCE_LIST_PAGE_SIZE}
        loading={loading}
        onPageChange={(nextPage) => setPage(nextPage)}
      />

      {deleteTarget && (
        <ConfirmDialog
          title="Delete Study Source"
          description={`Delete ${deleteTarget.studyCode} ${CONNECTION_LABELS[deleteTarget.connectionType]}? The SharePoint or Teams files will not be deleted.`}
          confirmLabel="Delete"
          confirmDanger
          submitting={deleting}
          onConfirm={() => void confirmDelete()}
          onCancel={() => {
            if (!deleting) setDeleteTarget(null);
          }}
        />
      )}
      {membersTarget && (
        <StudySourceMembersModal source={membersTarget} onClose={() => setMembersTarget(null)} />
      )}
    </AdminLayout>
  );
}
